import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import { getMonthlyEvents, getUpcomingEvents } from '../../api/calendar';

const EVENT_COLORS = {
  expiry:    { bg: '#EF4444', light: 'rgba(239,68,68,0.1)',   text: '#EF4444', label: 'Contract Expiry' },
  renewal:   { bg: '#F59E0B', light: 'rgba(245,158,11,0.1)', text: '#F59E0B', label: 'Renewal Due' },
  payment:   { bg: '#3B82F6', light: 'rgba(59,130,246,0.1)', text: '#3B82F6', label: 'Payment Due' },
  quotation: { bg: '#8B5CF6', light: 'rgba(139,92,246,0.1)', text: '#8B5CF6', label: 'Quotation Expiry' },
  service:   { bg: '#10B981', light: 'rgba(16,185,129,0.1)', text: '#10B981', label: 'Service Due' },
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ day: daysInPrevMonth - i, currentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, currentMonth: true });
  }
  const remaining = 42 - days.length;
  for (let d = 1; d <= remaining; d++) {
    days.push({ day: d, currentMonth: false });
  }
  return days;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function padZero(n) {
  return String(n).padStart(2, '0');
}

export default function CalendarPage() {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [monthEvents, setMonthEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterDays, setFilterDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const navigate = useNavigate();

  const fetchMonthly = useCallback(async () => {
    try {
      const res = await getMonthlyEvents(currentYear, currentMonth + 1);
      setMonthEvents(res.data || []);
    } catch {
      setMonthEvents([]);
    }
  }, [currentYear, currentMonth]);

  const fetchUpcoming = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUpcomingEvents(filterDays, filterType);
      setUpcomingEvents(res.data || []);
    } catch {
      setUpcomingEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filterDays, filterType]);

  useEffect(() => { fetchMonthly(); }, [fetchMonthly]);
  useEffect(() => { fetchUpcoming(); }, [fetchUpcoming]);

  const goToPrev = () => {
    if (currentMonth === 0) { setCurrentYear(y => y - 1); setCurrentMonth(11); }
    else setCurrentMonth(m => m - 1);
  };
  const goToNext = () => {
    if (currentMonth === 11) { setCurrentYear(y => y + 1); setCurrentMonth(0); }
    else setCurrentMonth(m => m + 1);
  };
  const goToToday = () => { setCurrentYear(today.getFullYear()); setCurrentMonth(today.getMonth()); };

  const calendarDays = buildCalendarDays(currentYear, currentMonth);

  const getEventsForDay = (day) => {
    const dateStr = `${currentYear}-${padZero(currentMonth + 1)}-${padZero(day)}`;
    return monthEvents.filter(e => e.date === dateStr);
  };

  const todayStr = `${today.getFullYear()}-${padZero(today.getMonth() + 1)}-${padZero(today.getDate())}`;

  const selectedDayEvents = selectedDay
    ? monthEvents.filter(e => e.date === `${currentYear}-${padZero(currentMonth + 1)}-${padZero(selectedDay)}`)
    : [];

  const stats = {
    expiry: upcomingEvents.filter(e => e.type === 'expiry').length,
    payment: upcomingEvents.filter(e => e.type === 'payment').length,
    service: upcomingEvents.filter(e => e.type === 'service').length,
    quotation: upcomingEvents.filter(e => e.type === 'quotation').length,
  };

  return (
    <AppLayout title="Event Calendar" breadcrumbs="Calendar">
      {/* Sub Nav */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{ background: 'white', borderBottom: '1px solid #E5E5E0', padding: '0 40px', gap: '32px' }}
      >
        {[{ key: 'list', label: 'List View' }, { key: 'calendar', label: 'Calendar View' }].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: activeTab === tab.key ? '3px solid #043959' : '3px solid transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'calendar' && (
      <div style={{ padding: '32px 0' }}>

        {/* Legend card */}
        <div className="bg-white rounded-2xl mb-4" style={{ border: '1px solid #E5E5E0', padding: '16px 24px' }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h3 className="text-base font-semibold" style={{ color: '#1A1A1A', margin: 0 }}>
              Event Calendar — {MONTH_NAMES[currentMonth]} {currentYear}
            </h3>
            <div className="flex flex-wrap gap-4" style={{ fontSize: '13px' }}>
              {Object.entries(EVENT_COLORS).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: val.bg }} />
                  <span style={{ color: '#666' }}>{val.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-2xl mb-4" style={{ border: '1px solid #E5E5E0', padding: '24px' }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ color: '#1A1A1A' }}>
              {MONTH_NAMES[currentMonth]} {currentYear}
            </h2>
            <div className="flex gap-2">
              {[['← Previous', goToPrev], ['Today', goToToday], ['Next →', goToNext]].map(([label, fn]) => (
                <button
                  key={label}
                  onClick={fn}
                  className="text-sm font-medium rounded-lg cursor-pointer transition-colors"
                  style={{ padding: '8px 14px', border: '1px solid #E5E5E0', background: 'white', color: '#1A1A1A', fontFamily: "'Lexend', sans-serif" }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center uppercase text-xs font-semibold" style={{ padding: '8px', color: '#999' }}>{d}</div>
            ))}
            {calendarDays.map((cell, idx) => {
              const events = cell.currentMonth ? getEventsForDay(cell.day) : [];
              const dateStr = cell.currentMonth
                ? `${currentYear}-${padZero(currentMonth + 1)}-${padZero(cell.day)}`
                : null;
              const isToday = dateStr === todayStr;
              const isSelected = selectedDay === cell.day && cell.currentMonth;

              return (
                <div
                  key={idx}
                  onClick={() => cell.currentMonth && setSelectedDay(isSelected ? null : cell.day)}
                  style={{
                    minHeight: 72,
                    borderRadius: 8,
                    border: isSelected ? '2px solid #043959' : '1px solid #F0F0EB',
                    padding: '6px',
                    cursor: cell.currentMonth ? 'pointer' : 'default',
                    opacity: cell.currentMonth ? 1 : 0.3,
                    background: isToday ? '#043959' : events.length > 0 ? 'rgba(4,57,89,0.02)' : 'white',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    className="text-sm font-semibold mb-1"
                    style={{ color: isToday ? 'white' : '#1A1A1A' }}
                  >
                    {cell.day}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {events.slice(0, 3).map((ev, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 9,
                          padding: '2px 4px',
                          borderRadius: 3,
                          background: EVENT_COLORS[ev.type]?.bg || '#666',
                          color: 'white',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {ev.title}
                      </div>
                    ))}
                    {events.length > 3 && (
                      <div style={{ fontSize: 9, color: '#999', fontWeight: 600 }}>+{events.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected day popover */}
          {selectedDay && selectedDayEvents.length > 0 && (
            <div className="mt-4 rounded-xl" style={{ border: '1px solid #E5E5E0', padding: '16px', background: '#FAFAF8' }}>
              <h4 className="text-sm font-semibold mb-3" style={{ color: '#1A1A1A' }}>
                Events on {MONTH_NAMES[currentMonth]} {selectedDay}, {currentYear}
              </h4>
              <div className="flex flex-col gap-2">
                {selectedDayEvents.map((ev, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-lg" style={{ padding: '10px 14px', border: '1px solid #E5E5E0' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: EVENT_COLORS[ev.type]?.bg, flexShrink: 0 }} />
                    <div className="flex-1">
                      <div className="text-sm font-medium" style={{ color: '#1A1A1A' }}>{ev.title}</div>
                      <div className="text-xs" style={{ color: '#999' }}>{ev.vendor} · {ev.details}</div>
                    </div>
                    <span className="text-xs font-semibold" style={{ padding: '3px 10px', borderRadius: 12, background: EVENT_COLORS[ev.type]?.light, color: EVENT_COLORS[ev.type]?.text }}>
                      {EVENT_COLORS[ev.type]?.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Upcoming Events table */}
        <div className="bg-white rounded-2xl mb-4" style={{ border: '1px solid #E5E5E0', padding: '24px' }}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h3 className="text-base font-semibold" style={{ color: '#1A1A1A' }}>Upcoming Events</h3>
            <div className="flex flex-wrap gap-3">
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #E5E5E0', borderRadius: 8, fontSize: 14, fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
              >
                <option value="">All Events</option>
                <option value="expiry">Contract Expiry</option>
                <option value="renewal">Renewal Due</option>
                <option value="payment">Payment Due</option>
                <option value="quotation">Quotation Expiry</option>
                <option value="service">Service Due</option>
              </select>
              <select
                value={filterDays}
                onChange={e => setFilterDays(Number(e.target.value))}
                style={{ padding: '8px 12px', border: '1px solid #E5E5E0', borderRadius: 8, fontSize: 14, fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
              >
                <option value={7}>Next 7 Days</option>
                <option value={30}>Next 30 Days</option>
                <option value={60}>Next 60 Days</option>
                <option value={90}>Next 90 Days</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                  {['Date', 'Event Type', 'Service / Item', 'Vendor / Party', 'Details', 'Days Left'].map(h => (
                    <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.5px', color: '#666' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center text-sm" style={{ padding: '40px 16px', color: '#999' }}>Loading events...</td></tr>
                ) : upcomingEvents.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-sm" style={{ padding: '40px 16px', color: '#999' }}>No upcoming events</td></tr>
                ) : upcomingEvents.map((ev, i) => {
                  const color = EVENT_COLORS[ev.type] || EVENT_COLORS.service;
                  return (
                    <tr
                      key={ev.id || i}
                      style={{ borderBottom: '1px solid #F0F0EB' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#FAFAF8'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>{formatDate(ev.date)}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: color.light, color: color.text }}>
                          {color.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 14, color: '#1A1A1A' }}>{ev.title}</td>
                      <td style={{ padding: '14px 16px', fontSize: 14, color: '#666' }}>{ev.vendor}</td>
                      <td style={{ padding: '14px 16px', fontSize: 14, color: '#666' }}>{ev.details}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: ev.days_remaining <= 7 ? 'rgba(239,68,68,0.1)' : ev.days_remaining <= 14 ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
                          color: ev.days_remaining <= 7 ? '#EF4444' : ev.days_remaining <= 14 ? '#F59E0B' : '#10B981',
                        }}>
                          {ev.days_remaining} days
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { label: 'Contracts Expiring', value: stats.expiry, color: '#EF4444', lightColor: 'rgba(239,68,68,0.1)', icon: '⏰' },
            { label: 'Payments Due', value: stats.payment, color: '#3B82F6', lightColor: 'rgba(59,130,246,0.1)', icon: '💰' },
            { label: 'Service Scheduled', value: stats.service, color: '#10B981', lightColor: 'rgba(16,185,129,0.1)', icon: '🔧' },
            { label: 'Quotations Expiring', value: stats.quotation, color: '#8B5CF6', lightColor: 'rgba(139,92,246,0.1)', icon: '📄' },
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-2xl" style={{ border: '1px solid #E5E5E0', padding: '20px 24px' }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>{stat.value}</div>
                  <div className="text-sm mt-1" style={{ color: '#666' }}>{stat.label}</div>
                </div>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: stat.lightColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                  {stat.icon}
                </div>
              </div>
              <div className="text-xs mt-3" style={{ color: '#999' }}>Next {filterDays} days</div>
            </div>
          ))}
        </div>

        </div>
      )}

      {/* ── LIST VIEW ── */}
      {activeTab === 'list' && (
        <div style={{ padding: '32px 0' }}>

          {/* Filters */}
          <div className="bg-white rounded-2xl mb-4" style={{ border: '1px solid #E5E5E0', padding: '16px 24px' }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-base font-semibold" style={{ color: '#1A1A1A' }}>All Events</h3>
              <div className="flex flex-wrap gap-3">
                <select
                  value={filterType}
                  onChange={e => setFilterType(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #E5E5E0', borderRadius: 8, fontSize: 14, fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value="">All Event Types</option>
                  <option value="expiry">Contract Expiry</option>
                  <option value="renewal">Renewal Due</option>
                  <option value="payment">Payment Due</option>
                  <option value="quotation">Quotation Expiry</option>
                  <option value="service">Service Due</option>
                </select>
                <select
                  value={filterDays}
                  onChange={e => setFilterDays(Number(e.target.value))}
                  style={{ padding: '8px 12px', border: '1px solid #E5E5E0', borderRadius: 8, fontSize: 14, fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value={7}>Next 7 Days</option>
                  <option value={30}>Next 30 Days</option>
                  <option value={60}>Next 60 Days</option>
                  <option value={90}>Next 90 Days</option>
                </select>
              </div>
            </div>
          </div>

          {/* Grouped list */}
          {loading ? (
            <div className="text-center text-sm py-10" style={{ color: '#999' }}>Loading events...</div>
          ) : upcomingEvents.length === 0 ? (
            <div className="bg-white rounded-2xl text-center text-sm py-16" style={{ border: '1px solid #E5E5E0', color: '#999' }}>
              No events in the selected period
            </div>
          ) : (() => {
            const grouped = upcomingEvents.reduce((acc, ev) => {
              if (!acc[ev.date]) acc[ev.date] = [];
              acc[ev.date].push(ev);
              return acc;
            }, {});

            return Object.entries(grouped).map(([date, events]) => {
              const d = new Date(date);
              const isToday = date === `${today.getFullYear()}-${padZero(today.getMonth() + 1)}-${padZero(today.getDate())}`;
              return (
                <div key={date} className="mb-4">
                  {/* Date header */}
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="flex flex-col items-center justify-center rounded-xl"
                      style={{
                        width: 52, height: 52, flexShrink: 0,
                        background: isToday ? '#043959' : '#F5F5F5',
                        color: isToday ? 'white' : '#1A1A1A',
                      }}
                    >
                      <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{d.getDate()}</span>
                      <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.7 }}>{d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
                        {d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        {isToday && <span className="ml-2 text-xs font-semibold" style={{ color: '#043959', background: 'rgba(4,57,89,0.08)', padding: '2px 8px', borderRadius: 12 }}>Today</span>}
                      </div>
                      <div className="text-xs" style={{ color: '#999' }}>{events.length} event{events.length > 1 ? 's' : ''}</div>
                    </div>
                  </div>

                  {/* Events for this date */}
                  <div className="flex flex-col gap-2 ml-16">
                    {events.map((ev, i) => {
                      const color = EVENT_COLORS[ev.type] || EVENT_COLORS.service;
                      return (
                        <div
                          key={i}
                          className="bg-white rounded-xl flex items-center gap-4"
                          style={{ border: '1px solid #E5E5E0', padding: '14px 18px', borderLeft: `4px solid ${color.bg}` }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>{ev.title}</span>
                              <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: color.light, color: color.text }}>
                                {color.label}
                              </span>
                            </div>
                            <div className="text-xs" style={{ color: '#666' }}>
                              {ev.vendor && <span className="mr-3">🏢 {ev.vendor}</span>}
                              {ev.details && <span>{ev.details}</span>}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <span
                              className="text-xs font-semibold"
                              style={{
                                padding: '4px 12px', borderRadius: 12,
                                background: ev.days_remaining <= 7 ? 'rgba(239,68,68,0.1)' : ev.days_remaining <= 14 ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
                                color: ev.days_remaining <= 7 ? '#EF4444' : ev.days_remaining <= 14 ? '#F59E0B' : '#10B981',
                              }}
                            >
                              {ev.days_remaining === 0 ? 'Today' : `${ev.days_remaining}d left`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

    </AppLayout>
  );
}
